"use client";

import {
  ArticleLayout,
  ArticleTOC,
  AnswerCard,
  KeyFactsBar,
  BenefitsGrid,
  CaseStudyCard,
  ComparisonTable,
  CtaBlock,
  DeliverablesBand,
  EffortTable,
  ExpertQuote,
  FAQList,
  GlossaryGrid,
  PillarsBand,
  PricingTiers,
  ProblemSolutionBlock,
  ProcessSteps,
  RelatedServicesGrid,
  ScopeGrid,
  SectionHead,
  ServiceHero,
  SocialProofQuote,
  TeamGrid,
  TimelineSteps,
  ToolsStack,
  UseCaseGrid,
  WhyDbiCards,
} from "@components/services";
import HeaderPublic from "@components/layout/HeaderPublic";
import FooterPublic from "@components/layout/FooterPublic";
import type { ReactNode } from "react";
import { ServiceData } from "@utils/serviceLoader";
import ROIAnalysisSection, { useROICalculator } from "./ROIAnalysisSection";

interface ServiceClientProps {
  serviceData: ServiceData;
  /** End-of-page hand-over into the Agentic BI Guide (`GuideReadNext`),
   *  rendered by the server page so the guide catalogue never ships to the
   *  client. Every service page has one — see guide-links.json. */
  guideSlot: ReactNode;
}

function assertData(serviceData: ServiceData) {
  if (!serviceData.hero) {
    throw new Error("ServiceClient: missing `hero` in service JSON.");
  }
  if (!serviceData.answer) {
    throw new Error("ServiceClient: missing `answer` in service JSON.");
  }
  if (!serviceData.scope) {
    throw new Error("ServiceClient: missing `scope` in service JSON.");
  }
  if (!serviceData.timeline) {
    throw new Error("ServiceClient: missing `timeline` in service JSON.");
  }
  if (!serviceData.deliverables) {
    throw new Error("ServiceClient: missing `deliverables` in service JSON.");
  }
  if (!serviceData.useCases) {
    throw new Error("ServiceClient: missing `useCases` in service JSON.");
  }
  if (!serviceData.case) {
    throw new Error("ServiceClient: missing `case` in service JSON.");
  }
  if (!serviceData.faq?.faqs) {
    throw new Error("ServiceClient: missing `faq.faqs` in service JSON.");
  }
  if (!serviceData.relatedServices) {
    throw new Error("ServiceClient: missing `relatedServices` in service JSON.");
  }
  if (!serviceData.ctaBlock) {
    throw new Error("ServiceClient: missing `ctaBlock` in service JSON.");
  }
}

export default function ServiceClient({ serviceData, guideSlot }: ServiceClientProps) {
  assertData(serviceData);

  const roiCalc = useROICalculator(serviceData.roiCalculator);

  if (!serviceData.serviceSubnav) {
    throw new Error("ServiceClient: missing `serviceSubnav` in service JSON.");
  }

  const subNavItems = serviceData.serviceSubnav.map(
    (item: { id: string; label: string; section: string }) => ({
      id: item.id,
      label: item.label,
      section: item.section,
    })
  );

  // Core sections — always present for every service page.
  const sectionMap: Record<string, JSX.Element> = {
    answer: (
      <section id="einleitung">
        <SectionHead
          eyebrow={serviceData.answer.eyebrow}
          title={serviceData.answer.title}
          blue
        />
        <AnswerCard
          question={serviceData.answer.question}
          paragraphs={serviceData.answer.paragraphs}
        />
        <KeyFactsBar facts={serviceData.answer.keyfacts} />
      </section>
    ),
    scope: (
      <section id="leistungen" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.scope.eyebrow}
          title={serviceData.scope.title}
          lede={serviceData.scope.lede}
          blue
        />
        <ScopeGrid items={serviceData.scope.items} />
      </section>
    ),
    timeline: (
      <section id="prozess" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.timeline.eyebrow}
          title={serviceData.timeline.title}
          lede={serviceData.timeline.lede}
          blue
        />
        <TimelineSteps
          steps={serviceData.timeline.steps}
          inlineCta={serviceData.timeline.inlineCta}
          className={serviceData.timeline.className}
        />
      </section>
    ),
    deliverables: (
      <section id="deliverables" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.deliverables.eyebrow}
          title={serviceData.deliverables.title}
          blue
        />
        <DeliverablesBand
          heading={serviceData.deliverables.heading}
          items={serviceData.deliverables.items}
        />
      </section>
    ),
    useCases: (
      <section id="usecases" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.useCases.eyebrow}
          title={serviceData.useCases.title}
          lede={serviceData.useCases.lede}
          blue
        />
        <UseCaseGrid items={serviceData.useCases.items} />
      </section>
    ),
    case: (
      <section id="case" style={{ marginTop: 64 }}>
        <SectionHead eyebrow={serviceData.case.eyebrow} title={serviceData.case.title} blue />
        <CaseStudyCard
          tag={serviceData.case.tag}
          title={serviceData.case.headline}
          quote={serviceData.case.quote}
          image={serviceData.case.image}
          imageAlt={serviceData.case.imageAlt}
          metrics={serviceData.case.metrics}
          cta={serviceData.case.cta}
        />
      </section>
    ),
    faq: (
      <section id="faq" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.faq.eyebrow}
          title={serviceData.faq.title}
          blue
        />
        <FAQList
          items={serviceData.faq.faqs.map((faq: { question: string; answer: string }) => ({
            question: faq.question,
            answer: faq.answer,
          }))}
        />
      </section>
    ),
  };

  if (serviceData.expertQuote?.quote && serviceData.expertQuote?.author) {
    if (!serviceData.expertQuote.eyebrow || !serviceData.expertQuote.title) {
      throw new Error(
        "ServiceClient: missing `expertQuote.eyebrow` or `expertQuote.title` in service JSON."
      );
    }
    sectionMap.expertQuote = (
      <section id="expert-quote" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.expertQuote.eyebrow}
          title={serviceData.expertQuote.title}
          blue
          headingLevel="h3"
        />
        <ExpertQuote
          quote={serviceData.expertQuote.quote}
          author={serviceData.expertQuote.author}
          role={serviceData.expertQuote.role}
          phone={serviceData.expertQuote.phone}
          imagePath={serviceData.expertQuote.imagePath}
          ctaText={serviceData.expertQuote.ctaText}
          ctaHref={serviceData.expertQuote.ctaHref}
          authorHref={serviceData.expertQuote.authorHref}
          authorLinkedIn={serviceData.expertQuote.authorLinkedIn}
        />
      </section>
    );
  }

  // Optional sections — only registered when data is present, so the
  // subnav filter (`orderedSections`) skips them automatically for pages
  // that don't include them in their JSON.
  if (serviceData.pricingTiers?.tiers?.length) {
    sectionMap.pricing = (
      <section id="preise" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.pricingTiers.eyebrow}
          title={serviceData.pricingTiers.title}
          lede={serviceData.pricingTiers.lede}
          blue
        />
        <PricingTiers layout="grid" tiers={serviceData.pricingTiers.tiers} />
      </section>
    );
  }

  if (serviceData.toolsStack?.tools?.length) {
    sectionMap.toolsStack = (
      <section id="tools-stack" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.toolsStack.eyebrow}
          title={serviceData.toolsStack.title}
          blue
        />
        <ToolsStack
          tools={serviceData.toolsStack.tools}
          intro={serviceData.toolsStack.intro}
          migrationNote={serviceData.toolsStack.migrationNote}
        />
      </section>
    );
  }

  if (serviceData.effortTable?.blocks?.length) {
    sectionMap.effortTable = (
      <section id="aufwand" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.effortTable.eyebrow}
          title={serviceData.effortTable.title}
          lede={serviceData.effortTable.lede}
          blue
        />
        <EffortTable
          blocks={serviceData.effortTable.blocks}
          total={serviceData.effortTable.total}
          labels={serviceData.effortTable.labels}
          cards={serviceData.effortTable.cards}
          scopeNote={serviceData.effortTable.scopeNote}
          note={serviceData.effortTable.note}
          footnotes={serviceData.effortTable.footnotes}
        />
      </section>
    );
  }

  if (serviceData.comparisonTable?.rows?.length) {
    sectionMap.comparison = (
      <section id="vergleich" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.comparisonTable.eyebrow}
          title={serviceData.comparisonTable.title}
          blue
        />
        <ComparisonTable
          rows={serviceData.comparisonTable.rows}
          columnLabels={serviceData.comparisonTable.columnLabels}
          highlightColumn={serviceData.comparisonTable.highlightColumn}
          intro={serviceData.comparisonTable.intro}
        />
        {serviceData.comparisonTable.ctaNote ? (
          <p className="comparison-cta-note">{serviceData.comparisonTable.ctaNote}</p>
        ) : null}
      </section>
    );
  }

  if (serviceData.testimonial?.items?.length) {
    sectionMap.testimonial = (
      // PLACEHOLDER: replace with real testimonial before launch
      <section id="kundenstimmen" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.testimonial.eyebrow}
          title={serviceData.testimonial.title}
          blue
        />
        <SocialProofQuote
          items={serviceData.testimonial.items}
          subtitle={serviceData.testimonial.subtitle}
          trustNote={serviceData.testimonial.trustNote}
        />
      </section>
    );
  }

  if (serviceData.glossary?.terms?.length) {
    sectionMap.glossary = (
      <section id="glossar" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.glossary.eyebrow}
          title={serviceData.glossary.title}
          blue
        />
        <GlossaryGrid
          terms={serviceData.glossary.terms}
          intro={serviceData.glossary.intro}
          references={serviceData.glossary.references}
        />
      </section>
    );
  }

  if (serviceData.team?.members?.length) {
    sectionMap.team = (
      <section id="team" style={{ marginTop: 64 }}>
        <SectionHead
          eyebrow={serviceData.team.eyebrow}
          title={serviceData.team.title}
          blue
          headingLevel="h3"
        />
        <TeamGrid
          members={serviceData.team.members}
          intro={serviceData.team.intro}
        />
      </section>
    );
  }

  const orderedSections: string[] = subNavItems
    .map((item: { section: string }) => item.section)
    .filter((key: string) => sectionMap[key]);

  const expertQuoteInNav = orderedSections.includes("expertQuote");

  const extraSections: JSX.Element[] = [];
  let expertQuoteRenderedAfterWhy = false;

  if (Array.isArray(serviceData.pillars) && serviceData.pillars.length > 0) {
    extraSections.push(
      <section id="migration-pillars" style={{ marginTop: 64 }} key="migration-pillars">
        <SectionHead
          eyebrow="Zusätzlicher Kontext"
          title="Migrationsergebnisse auf einen Blick"
          blue
        />
        <PillarsBand pillars={serviceData.pillars} />
      </section>
    );
  }

  if (serviceData.whyPowerBI?.sections?.length) {
    if (!serviceData.whyPowerBI.seoChip || !serviceData.whyPowerBI.titleLine1) {
      throw new Error(
        "ServiceClient: missing `whyPowerBI.seoChip` or `whyPowerBI.titleLine1` in service JSON."
      );
    }
    extraSections.push(
      <section id="why-section" style={{ marginTop: 64 }} key="why-section">
        <SectionHead
          eyebrow={serviceData.whyPowerBI.seoChip}
          title={serviceData.whyPowerBI.titleLine1}
          lede={serviceData.whyPowerBI.description}
          blue
          headingLevel="h3"
        />
        <WhyDbiCards
          cards={serviceData.whyPowerBI.sections.map((item: { id: string; title: string; description: string }) => ({
            id: item.id,
            title: item.title,
            description: item.description,
          }))}
        />
      </section>
    );
    if (
      serviceData.expertQuote?.quote &&
      serviceData.expertQuote?.author &&
      !expertQuoteInNav &&
      sectionMap.expertQuote
    ) {
      extraSections.push(
        <div key="expert-quote-after-why">{sectionMap.expertQuote}</div>
      );
      expertQuoteRenderedAfterWhy = true;
    }
  }

  if (serviceData.benefits?.items?.length) {
    if (!serviceData.benefits.seoChip || !serviceData.benefits.titleLine1) {
      throw new Error(
        "ServiceClient: missing `benefits.seoChip` or `benefits.titleLine1` in service JSON."
      );
    }
    extraSections.push(
      <section id="benefits-section" style={{ marginTop: 64 }} key="benefits-section">
        <SectionHead
          eyebrow={serviceData.benefits.seoChip}
          title={serviceData.benefits.titleLine1}
          lede={serviceData.benefits.description}
          blue
        />
        <BenefitsGrid items={serviceData.benefits.items} />
      </section>
    );
  }

  if (serviceData.problemSolution?.problem && serviceData.problemSolution?.solution) {
    if (!serviceData.problemSolution.seoChip || !serviceData.problemSolution.titleLine1) {
      throw new Error(
        "ServiceClient: missing `problemSolution.seoChip` or `problemSolution.titleLine1` in service JSON."
      );
    }
    extraSections.push(
      <section
        id="problem-solution-section"
        style={{ marginTop: 64 }}
        key="problem-solution-section"
      >
        <SectionHead
          eyebrow={serviceData.problemSolution.seoChip}
          title={serviceData.problemSolution.titleLine1}
          blue
        />
        <ProblemSolutionBlock
          problem={serviceData.problemSolution.problem}
          solution={serviceData.problemSolution.solution}
        />
      </section>
    );
  }

  if (serviceData.supportProcess?.steps?.length) {
    if (!serviceData.supportProcess.seoChip || !serviceData.supportProcess.titleLine1) {
      throw new Error(
        "ServiceClient: missing `supportProcess.seoChip` or `supportProcess.titleLine1` in service JSON."
      );
    }
    extraSections.push(
      <section
        id="support-process-section"
        style={{ marginTop: 64 }}
        key="support-process-section"
      >
        <SectionHead
          eyebrow={serviceData.supportProcess.seoChip}
          title={serviceData.supportProcess.titleLine1}
          blue
        />
        <ProcessSteps
          steps={serviceData.supportProcess.steps.map(
            (step: { title: string; description: string }, index: number) => ({
              number: index + 1,
              title: step.title,
              description: step.description,
            })
          )}
        />
      </section>
    );
  }

  if (serviceData.caseStudy?.roiAnalysis) {
    extraSections.push(
      <section id="roi-calculator" style={{ marginTop: 64 }} key="roi-calculator">
        {serviceData.roiCalculator && (
          <SectionHead
            eyebrow={serviceData.roiCalculator.seoChip}
            title={serviceData.roiCalculator.titleLine1}
            lede={serviceData.roiCalculator.description}
            blue
          />
        )}
        <ROIAnalysisSection
          roiAnalysis={serviceData.caseStudy.roiAnalysis}
          roiCalculatorConfig={serviceData.roiCalculator}
          roiCalc={roiCalc}
          reportPages={roiCalc.reportPages}
          hoursPerMonth={roiCalc.hoursPerMonth}
          hourlyRate={roiCalc.hourlyRate}
        />
      </section>
    );
  }

  if (
    serviceData.expertQuote?.quote &&
    serviceData.expertQuote?.author &&
    !expertQuoteInNav &&
    !expertQuoteRenderedAfterWhy &&
    sectionMap.expertQuote
  ) {
    extraSections.push(
      <div key="expert-quote-fallback-end">{sectionMap.expertQuote}</div>
    );
  }

  return (
    <>
      <HeaderPublic />
      <ServiceHero hero={serviceData.hero} />
      <ArticleLayout
        toc={
          <ArticleTOC
            items={subNavItems.map((item: { id: string; label: string }) => ({
              id: item.id,
              label: item.label,
            }))}
          />
        }
      >
        {orderedSections.map((section: string) => (
          <div key={section}>{sectionMap[section]}</div>
        ))}
        {extraSections}
        <section style={{ marginTop: 64 }}>
          <SectionHead eyebrow="Weiter erkunden" title="Verwandte Services" blue />
          <RelatedServicesGrid items={serviceData.relatedServices} />
        </section>
        {guideSlot}
        <section style={{ marginTop: 48 }}>
          <CtaBlock
            title={serviceData.ctaBlock.title}
            description={serviceData.ctaBlock.description}
            primaryCta={serviceData.ctaBlock.primaryCta}
            secondaryCta={serviceData.ctaBlock.secondaryCta}
          />
        </section>
      </ArticleLayout>
      <FooterPublic />
    </>
  );
}
